import { defineStore } from "pinia";
import { ref, type Ref } from "vue";
import { api } from "@/app/services/api.service";
import type { ProcessFilters } from "../types";

export const useInternalProcessesStore = defineStore("internalProcesses", () => {
  // Registration service state
  const registrationStream: Ref<any> = ref(null);
  const registrationPending: Ref<any> = ref(null);
  const registrationFailed: Ref<any> = ref(null);
  const registrationStatusChange: Ref<any> = ref(null);
  const registrationStatusError: Ref<any> = ref(null);

  // Incorporation service state
  const incorporationStream: Ref<any> = ref(null);
  const incorporationPending: Ref<any> = ref(null);
  const incorporationFailed: Ref<any> = ref(null);
  const incorporationStats: Ref<any> = ref(null);
  const incorporationStatusChange: Ref<any> = ref(null);
  const incorporationStatusError: Ref<any> = ref(null);
  const replaceSignedCopyStatusChange: Ref<any> = ref(null);
  const replaceSignedCopyError: Ref<any> = ref(null);
  const approvalActions: Ref<any> = ref(null);
  const incorporationDetails: Ref<any> = ref(null);

  // Name check state
  const incorporationNameCheck: Ref<any> = ref(null);
  const incorporationNameCheckError: Ref<any> = ref(null);
  const registrationNameCheck: Ref<any> = ref(null);
  const registrationNameCheckError: Ref<any> = ref(null);
  const buildingSocietyNameCheck: Ref<any> = ref(null);
  const buildingSocietyNameCheckError: Ref<any> = ref(null);

  // Legal documents state
  const documentStatusChange: Ref<any> = ref(null);
  const documentStatusError: Ref<any> = ref(null);

  // Annual returns state
  const annualReturnsStatusChange: Ref<any> = ref(null);
  const annualReturnsStatusError: Ref<any> = ref(null);

  // Form compliance state
  const formComplianceStatusChange: Ref<any> = ref(null);
  const formComplianceStatusError: Ref<any> = ref(null);

  const error: Ref<any> = ref(null);

  // Registration service actions
  const fetchRegistrationProcessesStream = (data: ProcessFilters) => {
    let path = `/business-registration/sync-items?status=${data.statuses}&page=${data.page}&per_page=${data.limit}`;
    if (data.search) path += `&search=${data.search}`;
    if (data.types) path += `&bnrKey=${data.types}`;
    if (data.startDate) path += `&start_date=${data.startDate}`;
    if (data.endDate) path += `&end_date=${data.endDate}`;
    api().get(path).then(r => {
      registrationStream.value = r.data;
    }).catch(e => {
      error.value = e.response?.data;
    });
  };

  const fetchRegistrationProcessesPending = () => {
    api().get("/business-registration/sync-items?status=Pending&page=1&per_page=12")
      .then(r => {
        registrationPending.value = r.data;
      }).catch(e => {
      error.value = e.response?.data;
    });
  };

  const fetchRegistrationProcessesFailed = () => {
    api().get("/business-registration/sync-items?status=Failed&page=1&per_page=12")
      .then(r => {
        registrationFailed.value = r.data;
      }).catch(e => {
      error.value = e.response?.data;
    });
  };

  const retryRegistrationProcess = (data: any) => {
    registrationStatusChange.value = null;
    api().post(`/business-registration/attachment/sync-item?brn=${data.brn}&sync-item=${data.description}`)
      .then(r => {
        registrationStatusChange.value = r.data;
      })
      .catch(e => {
        registrationStatusError.value = e.response?.data;
      });
  };

  const runRegistrationProcess = (data: { id: string }) => {
    registrationStatusChange.value = null;
    registrationStatusError.value = null;
    api().post("/business-registration/sync-items", { id: data.id })
      .then(r => {
        registrationStatusChange.value = r.data;
      })
      .catch(e => {
        registrationStatusError.value = e.response?.data;
      });
  };

  const rollbackNewRegistration = (data: { trackingNo: string; rationale: string }) => {
    return api().post("/business-registration/rollback-new-registration", {
      trackingNo: data.trackingNo,
      rationale: data.rationale,
    });
  };

  const replaceRegistrationAttachment = (data: { fileName: string; file: File }) => {
    const formData = new FormData();
    formData.append("fileName", data.fileName);
    formData.append("file", data.file);
    return api().post("/business-registration/attachment/replace", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  // Incorporation service actions
  const fetchIncorporationProcessesStream = (data: ProcessFilters) => {
    let path = `/incorporation/companies/get-all-actions?status=${data.statuses}&page=${data.page}&pageSize=${data.limit}`;
    if (data.search) path += `&search=${data.search}`;
    if (data.types) path += `&type=${data.types}`;
    if (data.startDate) path += `&startDate=${data.startDate}`;
    if (data.endDate) path += `&endDate=${data.endDate}`;
    api().get(path).then(r => {
      incorporationStream.value = r.data;
    }).catch(e => {
      error.value = e.response?.data;
    });
  };

  const fetchIncorporationProcessesPending = () => {
    api().get("/incorporation/companies/get-all-actions?status=Pending&page=1&pageSize=12")
      .then(r => {
        incorporationPending.value = r.data;
      }).catch(e => {
      error.value = e.response?.data;
    });
  };

  const fetchIncorporationProcessesFailed = () => {
    api().get("/incorporation/companies/get-all-actions?status=Expired&page=1&pageSize=12")
      .then(r => {
        incorporationFailed.value = r.data;
      }).catch(e => {
      error.value = e.response?.data;
    });
  };

  const fetchIncorporationProcessStatistics = () => {
    api().get("/incorporation/companies/get-action_statistics")
      .then(r => {
        incorporationStats.value = r.data.data;
      }).catch(e => {
      error.value = e.response?.data;
    });
  };

  const updateProcessStatus = (data: any) => {
    incorporationStatusChange.value = null;
    api().post("/incorporation/companies/update-action-status", data)
      .then(r => {
        incorporationStatusChange.value = r.data;
      })
      .catch(e => {
        incorporationStatusError.value = e.response?.data;
      });
  };

  const runProcessNow = (data: any) => {
    incorporationStatusChange.value = null;
    api().post("/incorporation/companies/run-action-immediately", data)
      .then(r => {
        incorporationStatusChange.value = r.data;
      })
      .catch(e => {
        incorporationStatusError.value = e.response?.data;
      });
  };

  const replaceSignedCopy = (data: { file: File; reason: string; fileName: string }) => {
    replaceSignedCopyStatusChange.value = null;
    replaceSignedCopyError.value = null;
    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("reason", data.reason);
    formData.append("fileName", data.fileName);
    api().post("/incorporation/companies/continuous-filing/replace-signed-copy", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then(r => {
        replaceSignedCopyStatusChange.value = r.data;
      })
      .catch(e => {
        replaceSignedCopyError.value = e.response?.data;
      });
  };

  // view process details incorporation/companies/approval-actions/3c266958-b550-4fea-861b-48218e76d319
  const fetchIncorporationProcessDetails = (data: any) => {
    api().get(`/incorporation/companies/approval-actions/${data.id}`)
      .then(r => {
        incorporationDetails.value = r.data;
      }).catch(e => {
      error.value = e.response?.data;
    });
  };

  // Name check actions
  const incorporationPushToNameCheck = (data: any) => {
    incorporationNameCheck.value = null;
    api().post("/incorporation/companies/registry/name-search/manual-update", data)
      .then(r => {
        incorporationNameCheck.value = r.data;
      })
      .catch(e => {
        incorporationNameCheckError.value = e.response?.data;
      });
  };

  const registrationPushToNameCheck = (data: any) => {
    registrationNameCheck.value = null;
    api().post("/business-registration/sync-name-check", data)
      .then(r => {
        registrationNameCheck.value = r.data;
      })
      .catch(e => {
        registrationNameCheckError.value = e.response?.data;
      });
  };

  const buildingSocietyPushToNameCheck = (data: any) => {
    buildingSocietyNameCheck.value = null;
    api().post(`/building-societies/societies/registry/${data}/manual-publish`, data)
      .then(r => {
        buildingSocietyNameCheck.value = r.data;
      })
      .catch(e => {
        buildingSocietyNameCheckError.value = e.response?.data;
      });
  };

  // Legal document action
  const retryDocumentProcess = (instrumentNumber: string) => {
    documentStatusChange.value = null;
    api().post("/legal-documents/attachments/retry?instrumentNo=" + instrumentNumber)
      .then(r => {
        documentStatusChange.value = r.data;
      })
      .catch(e => {
        documentStatusError.value = e.response?.data;
      });
  };

  // Annual returns action
  const updateAnnualReturnStatus = (data: any) => {
    annualReturnsStatusChange.value = null;
    api().post("/incorporation/annual-returns/mark-years-as-filed/" + data.brn, data)
      .then(r => {
        annualReturnsStatusChange.value = r.data;
      })
      .catch(e => {
        annualReturnsStatusError.value = e.response?.data;
      });
  };

  // Form compliance action
  const updateFormCompliance = (data: any) => {
    formComplianceStatusChange.value = null;
    api().post("/incorporation/company-files/compliance-checks", data)
      .then(r => {
        formComplianceStatusChange.value = r.data;
      })
      .catch(e => {
        formComplianceStatusError.value = e.response?.data;
      });
  };

  return {
    registrationStream, registrationPending, registrationFailed,
    registrationStatusChange, registrationStatusError,
    incorporationStream, incorporationPending, incorporationFailed,
    incorporationStats, incorporationStatusChange, incorporationStatusError,
    replaceSignedCopyStatusChange, replaceSignedCopyError,
    approvalActions, incorporationDetails, incorporationNameCheck, incorporationNameCheckError,
    registrationNameCheck, registrationNameCheckError,
    buildingSocietyNameCheck, buildingSocietyNameCheckError,
    documentStatusChange, documentStatusError,
    annualReturnsStatusChange, annualReturnsStatusError,
    formComplianceStatusChange, formComplianceStatusError,
    error,
    fetchRegistrationProcessesStream, fetchRegistrationProcessesPending, fetchRegistrationProcessesFailed,
    retryRegistrationProcess, runRegistrationProcess, rollbackNewRegistration, replaceRegistrationAttachment,
    fetchIncorporationProcessesStream, fetchIncorporationProcessesPending, fetchIncorporationProcessesFailed,
    fetchIncorporationProcessStatistics, fetchIncorporationProcessDetails, updateProcessStatus, runProcessNow,
    replaceSignedCopy,
    incorporationPushToNameCheck, registrationPushToNameCheck, buildingSocietyPushToNameCheck,
    retryDocumentProcess, updateAnnualReturnStatus, updateFormCompliance
  };
});
